import React from 'react'
import PropTypes from 'prop-types'
import ReactModal from 'react-modal'
import classNames from 'classnames'

import styles from './modal-container.css'

/**
 * Reusable controlled modal container whose visibility and close behavior are supplied by the caller.
 * Additional props are forwarded to ReactModal.
 *
 * @param {React.ReactNode} props.children: Content rendered inside the modal.
 * @param {boolean} [props.isOpen=true]: Controls whether the modal is visible.
 * @param {Function} [props.onRequestClose]: Called when ReactModal requests dismissal.
 * @param {boolean} [props.shouldCloseOnOverlayClick=false]: Closes the modal on overlay click when true.
 * @param {boolean} [props.noBackgroundColor=false]: Removes the modal background color when true.
 * @param {boolean} [props.darkTheme=false]: Applies the dark theme when true.
 * @returns {React.ReactElement} The rendered modal container.
 */
function ModalContainer({
  children,
  isOpen,
  onRequestClose,
  shouldCloseOnOverlayClick,
  noBackgroundColor,
  darkTheme,
  ...props
}) {
  return (
    <ReactModal
      className={{
        base: classNames({
          [styles.modal]: true,
          [styles.noBackgroundColor]: noBackgroundColor,
          [styles.darkTheme]: darkTheme
        }),
        afterOpen: styles.modalAfterOpen,
        beforeClose: styles.modalBeforeClose
      }}
      overlayClassName={{
        base: styles.overlay,
        afterOpen: styles.overlayAfterOpen,
        beforeClose: styles.overlayBeforeClose
      }}
      contentLabel="Modal"
      {...props}
      isOpen={isOpen}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldCloseOnEsc={Boolean(onRequestClose)}
      onRequestClose={onRequestClose}
    >
      {children}
    </ReactModal>
  )
}

ModalContainer.propTypes = {
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool,
  onRequestClose: PropTypes.func,
  shouldCloseOnOverlayClick: PropTypes.bool,
  noBackgroundColor: PropTypes.bool,
  darkTheme: PropTypes.bool
}

ModalContainer.defaultProps = {
  isOpen: true,
  onRequestClose: null,
  shouldCloseOnOverlayClick: false,
  noBackgroundColor: false,
  darkTheme: false
}

export default ModalContainer
